import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchGallery } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Calendar, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import SeoHead from '@/components/Seo';

const BASE_URL = `https://api.ibadansouthanglicandiocese.org.ng/`;
const DEFAULT_LOGO = `https://res.cloudinary.com/dwrqyrm84/image/upload/v1749389564/mq1jwwqt9xxehogbwlem.png`;

const AlbumThumbnail = ({ src, alt, className = "" }) => {
    const getFullSrc = () => {
        if (!src) return DEFAULT_LOGO;
        if (src.startsWith('http')) return src;
        return `${BASE_URL}${src}`;
    };

    return (
        <img
            src={getFullSrc()}
            alt={alt || "Gallery thumbnail"}
            className={className}
            onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_LOGO; }}
        />
    );
};

interface GalleryAlbum {
    id: number;
    title: string;
    date: string;
    images: number;
    thumbnail: string;
}

const Gallery = () => {
    const [galleryItems, setGalleryItems] = useState<GalleryAlbum[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    useEffect(() => {
        const getGallery = async () => {
            try {
                const response = await fetchGallery();
                const data = response.data;
                const formatted = data.map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    date: item.date,
                    images: item.images_count || 0, // Ensure this matches your API key
                    thumbnail: item.thumbnail,
                }));
                setGalleryItems(formatted);
            } catch (err) {
                toast({
                    title: "Archive Error",
                    description: "Unable to retrieve the photo gallery.",
                    variant: "destructive"
                });
            } finally {
                setLoading(false);
            }
        };
        getGallery();
    }, [toast]);

    return (
        <div className="min-h-screen bg-[#fcfaf7] py-24 px-6">
            <SeoHead
                title="Diocesan Archives | Ibadan South"
                description="Explore the visual history of our diocese. Photos from synods, parish anniversaries, and diocesan festivals."
            />

            <div className="max-w-7xl mx-auto">

                {/* Header Section */}
                <header className="max-w-3xl mx-auto text-center mb-24">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex justify-center mb-8"
                    >
                        <div className="p-4 bg-amber-600 rounded-[1.5rem] text-white shadow-xl shadow-amber-900/20">
                            <Camera className="w-8 h-8" />
                        </div>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-7xl font-serif font-bold text-slate-900 mb-8"
                    >
                        Diocesan <span className="text-amber-600">Archives</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-lg text-slate-500 font-serif italic leading-relaxed"
                    >
                        Capturing the growth, worship, and fellowship of the Ibadan South Anglican Diocese through the years.
                    </motion.p>
                </header>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="h-[450px] bg-white rounded-[3rem] animate-pulse border border-slate-100" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                        {galleryItems.map((album, index) => (
                            <motion.div
                                key={album.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Link to={`/gallery/${album.id}`} className="group block relative">
                                    {/* Stacked Paper Effect Backgrounds */}
                                    <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-sm translate-x-3 translate-y-3 transition-transform duration-500 group-hover:translate-x-5 group-hover:translate-y-5 border border-slate-100" />
                                    <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-md translate-x-1.5 translate-y-1.5 transition-transform duration-500 group-hover:translate-x-2.5 group-hover:translate-y-2.5 border border-slate-100" />

                                    <Card className="relative z-10 overflow-hidden rounded-[2.5rem] border-none shadow-2xl bg-white transition-all duration-700 group-hover:-translate-y-3">
                                        <CardContent className="p-0">
                                            <div className="relative h-72 overflow-hidden">
                                                <AlbumThumbnail
                                                    src={album.thumbnail}
                                                    alt={album.title}
                                                    className="w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-110"
                                                />

                                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

                                                <div className="absolute bottom-6 left-6">
                                                    <div className="bg-amber-600 text-white px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg">
                                                        <ImageIcon className="w-3 h-3" />
                                                        {album.images} Memories
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-10">
                                                <div className="flex items-center gap-2 text-slate-400 mb-4">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                                                        {album.date ? format(parseISO(album.date), 'MMMM d, yyyy') : 'Archived'}
                                                    </span>
                                                </div>

                                                <h3 className="text-2xl font-serif font-bold text-slate-900 leading-tight group-hover:text-amber-700 transition-colors line-clamp-2 mb-8">
                                                    {album.title}
                                                </h3>

                                                <div className="flex items-center text-amber-600/30 group-hover:text-amber-600 transition-colors">
                                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                                                        Enter Album
                                                    </span>
                                                    <div className="h-px flex-1 bg-slate-100 group-hover:bg-amber-100 mx-4" />
                                                    <div className="p-2 rounded-full bg-slate-50 group-hover:bg-amber-600 group-hover:text-white transition-all">
                                                        <ArrowRight className="w-4 h-4" />
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}

                {!loading && galleryItems.length === 0 && (
                    <div className="text-center py-40 bg-white rounded-[3rem] border-2 border-dashed border-slate-100">
                        <ImageIcon className="w-16 h-16 text-slate-200 mx-auto mb-6" />
                        <h3 className="text-2xl font-serif font-bold text-slate-400">The archives are being updated.</h3>
                        <p className="text-slate-300 italic font-serif mt-2">Please check back soon for new photo collections.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Gallery;